/**
 * Der Rahmen der drei Rechtsseiten — Impressum, Datenschutz,
 * Nutzungsbedingungen.
 *
 * Aufbau aus `templates/legal/Legal.dc.html` des Design-System-Projekts: eine
 * Karte auf ruhigem Grund, oben das Logo neben dem Titel, darunter Abschnitte
 * mit viel Zeilenabstand und einer Zeilenlänge, die man wirklich liest.
 *
 * **Ein Rahmen für alle drei und nicht drei gleiche Nachbauten** — dieselbe
 * Begründung wie bei `auth-layout.tsx`: Wer den einen ändert, lässt die anderen
 * stehen.
 *
 * **Diese Seiten stehen außerhalb der Anwendung**, auch für einen angemeldeten
 * Nutzer. Sie gehören weder einer Anlage noch einem Konto — die beiden Achsen,
 * nach denen die Seitenleiste geschnitten ist (HM-031) —, und ein Impressum in
 * einer Anlagenansicht wäre ein Dokument, das je nach Umschalter woanders
 * steht. Deshalb tragen sie einen eigenen Kopf mit dem Weg zurück.
 */
import { Card, Wordmark, useSidebarCollapsed } from "@hausmeter/ui";
import { Fragment, type ReactNode } from "react";
import { navigate, routePath, type LegalDocument, type Route } from "../lib/router.js";
import { HAS_OPEN_PLACEHOLDERS, LEGAL_AS_OF, OPEN_ENTRIES } from "../lib/legal.js";

/** Die drei Dokumente in der Reihenfolge, in der sie im Kopf stehen. */
const DOCUMENTS: readonly { readonly document: LegalDocument; readonly label: string }[] = [
  { document: "imprint", label: "Impressum" },
  { document: "privacy", label: "Datenschutz" },
  { document: "terms", label: "Nutzungsbedingungen" },
];

export interface LegalPageProps {
  readonly current: LegalDocument;
  readonly title: string;
  /** Die Zeile unter dem Titel — sie sagt, nach welcher Vorschrift der Text steht. */
  readonly lead: string;
  readonly children: ReactNode;
}

export function LegalPage({ current, title, lead, children }: LegalPageProps) {
  return (
    <main
      style={{
        minHeight: "100%",
        background: "var(--surface-page)",
        padding: "clamp(16px, 3vw, 48px)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "clamp(20px, 3vw, 32px)",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 900,
          display: "flex",
          flexDirection: "column",
          gap: "clamp(20px, 3vw, 32px)",
        }}
      >
        <Header current={current} />

        {/*
          `Card` aus dem Design-System und kein eigenes `div` mit denselben
          Werten: Grund, Haarlinie, Radius und Schatten stehen dort. Der Entwurf
          gibt der Rechtsseite einen kräftigeren Rand und einen tieferen
          Schatten als einer Karte im Dashboard — sie liegt allein auf der
          Fläche und soll wie ein Blatt wirken. Das sind die beiden einzigen
          Abweichungen, und sie stehen als Token da, nicht als Hexwerte.
        */}
        <Card
          padding="none"
          style={{
            borderColor: "var(--border-default)",
            boxShadow: "var(--shadow-md)",
            borderRadius: "var(--radius-xl)",
          }}
        >
          <article
            style={{
              padding: "clamp(24px, 4vw, 56px)",
              display: "flex",
              flexDirection: "column",
              gap: 28,
            }}
          >
            <Title title={title} lead={lead} />
            {children}
          </article>
        </Card>

        <Footer current={current} />
      </div>
    </main>
  );
}

/**
 * Der Kopf über der Karte: die Marke als Weg zurück in die Anwendung, daneben
 * die drei Dokumente.
 *
 * **Die Marke führt auf `/` und nicht `history.back()`.** Eine Rechtsseite wird
 * oft direkt aufgerufen — aus einer E-Mail, aus einem Suchergebnis, aus dem
 * Haken bei der Registrierung in einem neuen Tab. Dort gibt es kein „zurück",
 * und ein Knopf, der dann nichts tut, ist schlimmer als keiner.
 */
function Header({ current }: { readonly current: LegalDocument }) {
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "var(--sp-4)",
      }}
    >
      <a
        href="/"
        onClick={(event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
          event.preventDefault();
          navigate({ kind: "start" });
        }}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 11,
          textDecoration: "none",
        }}
      >
        <Wordmark
          size={19}
          logoSize={34}
          logo={
            <img src="/logo.png" alt="" width={34} height={34} style={{ objectFit: "contain" }} />
          }
        />
      </a>

      <LegalDocumentNav current={current} />
    </div>
  );
}

/**
 * Die drei Dokumente als Reiterzeile.
 *
 * Sie sieht aus wie `SegmentedControl` aus dem Design-System und ist bewusst
 * **keiner**: Der ist eine Radiogruppe — `role="radiogroup"`, `aria-checked`,
 * Pfeiltasten —, und das ist die richtige Form für „Tag · Woche · Monat", wo
 * man eine Ansicht **einstellt**. Hier wechselt man das **Dokument**: Jedes hat
 * eine eigene Adresse, gehört in den Verlauf und muss sich in einem neuen Tab
 * öffnen lassen. Das sind Links (HM-038), und eine Radiogruppe aus Links wäre
 * für jede Sprachausgabe eine Falschauskunft.
 *
 * Übernommen ist damit das Aussehen der Pillen, nicht das Bedienelement.
 */
export function LegalDocumentNav({ current }: { readonly current: LegalDocument }) {
  return (
    <nav
      aria-label="Rechtliche Hinweise"
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 4,
        padding: 4,
        alignSelf: "flex-start",
        width: "fit-content",
        borderRadius: "var(--radius-md)",
        background: "var(--surface-sunken)",
      }}
    >
      {DOCUMENTS.map((entry) => (
        <LegalTab key={entry.document} entry={entry} active={entry.document === current} />
      ))}
    </nav>
  );
}

function LegalTab({
  entry,
  active,
}: {
  readonly entry: (typeof DOCUMENTS)[number];
  readonly active: boolean;
}) {
  return (
    <LegalLink
      to={{ kind: "legal", document: entry.document }}
      /* Die aktuelle Seite ist kein Link ins Nichts, sondern die Marke dafür,
         wo man steht — deshalb bleibt sie klickbar und trägt `aria-current`. */
      ariaCurrent={active}
      /* Maße aus `ui_kits/dashboard/LegalScreen.jsx`: 6/12 im gemuldeten Feld,
         die aktive Pille auf Kartengrund mit dem kleinsten Schatten. */
      style={{
        padding: "6px 12px",
        borderRadius: "var(--radius-sm)",
        fontSize: 13,
        fontWeight: active ? "var(--fw-bold)" : "var(--fw-medium)",
        background: active ? "var(--surface-card)" : "transparent",
        boxShadow: active ? "var(--shadow-xs)" : "none",
        color: active ? "var(--text-strong)" : "var(--text-muted)",
        whiteSpace: "nowrap",
      }}
    >
      {entry.label}
    </LegalLink>
  );
}

/** Titel und Beisatz im Kopf der Karte, mit dem Logo daneben wie im Entwurf. */
function Title({ title, lead }: { readonly title: string; readonly lead: string }) {
  return (
    <header
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "var(--sp-4)",
        paddingBottom: 22,
        borderBottom: "1px solid var(--border-subtle)",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 40,
          height: 40,
          flex: "none",
          borderRadius: 11,
          background: "var(--surface-brand-strong)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Die Bildmarke ist für hellen Grund gezeichnet; auf dem Grün braucht
            sie ihre eigene helle Fläche — dieselbe Lösung wie im AuthLayout. */}
        <img
          src="/logo.png"
          alt=""
          width={34}
          height={34}
          style={{
            objectFit: "contain",
            background: "var(--surface-card)",
            borderRadius: 9,
          }}
        />
      </span>
      <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        <h1
          style={{
            margin: 0,
            fontFamily: "var(--font-display)",
            fontWeight: 800,
            fontSize: "clamp(24px, 2.6vw, 32px)",
            letterSpacing: "var(--tracking-tight)",
            color: "var(--text-strong)",
          }}
        >
          {title}
        </h1>
        <p style={{ margin: 0, fontSize: 13.5, color: "var(--text-muted)" }}>{lead}</p>
      </div>
    </header>
  );
}

/**
 * Die Fußzeile unter der Karte — Stand und, falls nötig, der eine Satz über
 * fehlende Angaben.
 */
function Footer({ current }: { readonly current: LegalDocument }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-2)" }}>
      {/* Siehe `LegalFooterLinks`: `--text-faint` trägt den Kontrast nicht. */}
      <p style={{ margin: 0, fontSize: 12.5, color: "var(--text-muted)" }}>
        Stand: {LEGAL_AS_OF}. Es gilt deutsches Recht.
      </p>
      {HAS_OPEN_PLACEHOLDERS && current !== "terms" && <OpenEntries />}
    </div>
  );
}

/**
 * **Ein Satz und nicht vier Platzhalter** (die Lehre aus HM-041).
 *
 * Der Entwurf setzt `[Telefonnummer]` und `[E-Mail-Adresse]` in den Fließtext.
 * Auf einer veröffentlichten Seite sieht das aus wie ein Fehler; schlimmer ist,
 * dass es sich nicht von einer echten Angabe unterscheidet, wenn jemand die
 * eckigen Klammern für Gestaltung hält. Die Zeilen fehlen deshalb ganz, und
 * hier steht **einmal**, warum — sichtbar genug, dass es niemand
 * versehentlich veröffentlicht.
 */
function OpenEntries() {
  return (
    <p
      style={{
        margin: 0,
        padding: "var(--sp-3) var(--sp-4)",
        borderRadius: "var(--radius-md)",
        border: "1px solid var(--border-default)",
        background: "var(--surface-inset)",
        fontSize: 12.5,
        lineHeight: 1.6,
        color: "var(--text-muted)",
      }}
    >
      Noch nicht eingetragen und deshalb oben nicht aufgeführt:{" "}
      {OPEN_ENTRIES.join(", ")}. Die Angaben stehen in{" "}
      <code style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>
        packages/legal/src
      </code>{" "}
      und sind vor einer Veröffentlichung zu ergänzen.
    </p>
  );
}

/**
 * Dieselben Texte **im Dashboard** — die Darstellung für einen angemeldeten
 * Nutzer (`ui_kits/dashboard/LegalScreen.jsx`).
 *
 * **Zwei Rahmen, ein Inhalt.** Wer angemeldet ist, soll das Impressum nicht in
 * einer Ansicht lesen, die aussieht, als hätte er die Anwendung verlassen — das
 * Kit legt es deshalb als Bereich in den Rahmen. Wer **nicht** angemeldet ist,
 * bekommt weiter `LegalPage`: Der Rahmen setzt eine Sitzung, eine Anlage und
 * eine Seitenleiste voraus, und § 5 DDG verlangt das Impressum unabhängig
 * davon. Denselben Text zweimal zu schreiben wäre die naheliegende und die
 * falsche Lösung — es ist genau die Doppelung, an der `auth-layout.tsx` einmal
 * gescheitert ist.
 *
 * **Ohne zweite Überschrift.** Das Kit setzt eine `h1` in den Bereich, obwohl
 * die Kopfzeile darüber denselben Titel trägt; hier trägt ihn `AppShell`, und
 * ein zweiter unmittelbar darunter benennt dieselbe Seite zweimal. Was der
 * Entwurf als `lead` in den Bereich schreibt, steht als Unterzeile der
 * Kopfzeile — an der Stelle, an der jeder andere Bereich seine hat.
 */
export function LegalScreen({
  current,
  children,
}: {
  readonly current: LegalDocument;
  readonly children: ReactNode;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--sp-5)",
        /* Kit: 820. Ein Rechtstext über die volle Breite eines
           Dashboard-Fensters ist nicht mehr zu lesen. */
        maxWidth: 820,
      }}
    >
      <LegalDocumentNav current={current} />
      {children}
      <p style={{ margin: 0, fontSize: 12.5, color: "var(--text-muted)" }}>
        Stand: {LEGAL_AS_OF}. Es gilt deutsches Recht.
      </p>
      {HAS_OPEN_PLACEHOLDERS && <OpenEntries />}
    </div>
  );
}

/**
 * Die Rechtslinks am Fuß der Seitenleiste — für den **angemeldeten** Nutzer
 * (HM-061).
 *
 * **Kein Eintrag in der Navigation und keiner im Kontomenü.** Die Seitenleiste
 * führt Bereiche der Anlage, das Kontomenü führt, was dem Konto gehört
 * (HM-031); ein Impressum gehört keinem von beiden — es ändert sich nicht, wenn
 * man oben die Anlage wechselt, und es hat nichts mit dem Profil zu tun. Als
 * Menüpunkt stünde es in einer Liste, deren Regel es bricht.
 *
 * **Weglassen ging trotzdem nicht.** § 5 DDG verlangt das Impressum „leicht
 * erkennbar, unmittelbar erreichbar und ständig verfügbar" — also nicht nur auf
 * der Anmeldeseite, die ein angemeldeter Nutzer nie wieder sieht. Deshalb steht
 * es dort, wo eine Fußzeile hingehört: unter allem, ohne Symbol und ohne Marke.
 */
export function LegalFooterLinks({ current }: { readonly current?: LegalDocument }) {
  /**
   * **Auf der eingeklappten Leiste stehen sie nicht.** Zwei Wörter in 11,5 px
   * auf 64 Pixeln Breite wären entweder abgeschnitten oder ein Symbol, das
   * niemand als Impressum liest — und ein Pflichtlink, den man nicht erkennt,
   * erfüllt § 5 DDG nicht. Er ist einen Klick weiter erreichbar: Der Schalter
   * zum Ausklappen steht am Kopf derselben Leiste, und „ständig verfügbar"
   * heißt nicht „ohne jede Bedienung sichtbar" — sonst dürfte auch die
   * Schublade auf einem Telefon die Links nicht verbergen.
   */
  if (useSidebarCollapsed()) return null;

  return (
    <nav
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 6,
        marginTop: "var(--sp-3)",
      }}
    >
      {/*
        **Nur zwei von drei.** Das Kit führt hier Impressum und Datenschutz —
        die beiden, die ein Gesetz verlangt und die jemand im Betrieb sucht. Die
        Nutzungsbedingungen stehen einen Klick weiter in der Reiterzeile; drei
        Links in einer 11,5-px-Fußzeile sind eine Zeile Kleingedrucktes, in der
        man nichts mehr findet.
      */}
      {DOCUMENTS.filter((entry) => entry.document !== "terms").map((entry, index) => {
        const active = entry.document === current;

        return (
          /*
            **`Fragment` und kein `<span>` um Trenner und Link.**

            Der Wrapper war ein `inline-flex` ohne `align-items`, also
            `stretch`. Der Trenner darin trug **keine** Schriftgröße und erbte
            damit die 14 px der Leiste, während der Link auf 11,5 steht: Der
            höhere Trenner bestimmte die Höhe des Wrappers, der Link wurde
            mitgezogen, und sein Text saß oben im gedehnten Kasten. Sichtbar war
            das als „Datenschutz steht etwas höher als Impressum" — die zweite
            Zeile hatte einen Trenner, die erste nicht.

            Flach gelegt sind alle Einträge direkte Kinder derselben Flex-Zeile,
            und deren `alignItems: "center"` gilt für jeden einzeln. So macht es
            auch das Kit (`ui_kits/dashboard/Sidebar.jsx`).
          */
          <Fragment key={entry.document}>
            {index > 0 && (
              <span
                aria-hidden="true"
                /* Dieselbe Größe wie der Link daneben — ein Trenner, der größer
                   ist als das, was er trennt, verschiebt die Grundlinie. */
                style={{ fontSize: 11.5, lineHeight: 1, color: "var(--border-strong)" }}
              >
                ·
              </span>
            )}
            <LegalLink
              to={{ kind: "legal", document: entry.document }}
              ariaCurrent={active}
              /* `--text-muted` und nicht `--text-faint`: Letzteres liegt auf
                 weißem Grund bei 2,56:1 und damit weit unter den 4,5:1, die
                 WCAG 2.1 AA für Fließtext verlangt. An einem Pflichtlink wiegt
                 das doppelt — § 5 DDG will das Impressum „leicht erkennbar". */
              style={{
                fontSize: 11.5,
                lineHeight: 1,
                fontWeight: active ? 600 : 500,
                color: active ? "var(--hm-green-700)" : "var(--text-muted)",
              }}
            >
              {entry.label}
            </LegalLink>
          </Fragment>
        );
      })}
    </nav>
  );
}

/* --------------------------------------------------------------------------
 * Bausteine für den Inhalt
 * ----------------------------------------------------------------------- */

/**
 * Ein nummerierter Abschnitt. Die Nummer steht **im Titel** und nicht als
 * `<ol>`-Zähler: Auf sie wird in Auskünften und Schriftsätzen verwiesen, und
 * ein Zähler, der sich beim Einfügen eines Abschnitts verschiebt, macht jeden
 * älteren Verweis falsch.
 */
export function LegalSection({
  title,
  children,
}: {
  readonly title: string;
  readonly children: ReactNode;
}) {
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--sp-2)" }}>
      <h2
        style={{
          margin: 0,
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: 17,
          color: "var(--text-strong)",
        }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

/**
 * Ein Absatz Fließtext.
 *
 * `max-width: 78ch` ist keine Vorliebe: Darüber verliert das Auge beim
 * Zeilenwechsel den Anschluss, und ein Rechtstext wird ohnehin ungern gelesen.
 */
export function LegalParagraph({ children }: { readonly children: ReactNode }) {
  return (
    <p
      style={{
        margin: 0,
        maxWidth: "78ch",
        fontSize: 14.5,
        lineHeight: 1.7,
        color: "var(--text-body)",
      }}
    >
      {children}
    </p>
  );
}

/** Eine Aufzählung im selben Maß wie der Fließtext. */
export function LegalList({ items }: { readonly items: readonly ReactNode[] }) {
  return (
    <ul
      style={{
        margin: 0,
        paddingLeft: "1.2em",
        maxWidth: "78ch",
        display: "flex",
        flexDirection: "column",
        gap: "var(--sp-2)",
        fontSize: 14.5,
        lineHeight: 1.7,
        color: "var(--text-body)",
      }}
    >
      {items.map((item, index) => (
        // Die Reihenfolge ist fest und der Inhalt statisch; ein anderer
        // Schlüssel als der Index wäre hier eine Erfindung.
        <li key={index}>{item}</li>
      ))}
    </ul>
  );
}

/**
 * Der hervorgehobene Kasten mit dem grünen Punkt — im Entwurf „Kurz gefasst".
 *
 * Er steht **einmal** je Seite: Was zweimal hervorgehoben ist, ist nicht mehr
 * hervorgehoben. Und er steht nur dort, wo er etwas sagt, das **nirgends sonst
 * vorn steht** — heute allein auf den Nutzungsbedingungen, für den Satz, dass
 * die angezeigten Werte nicht eichrechtlich sind.
 *
 * **Auf der Datenschutzerklärung gab es ihn und gibt es ihn nicht mehr.** Dort
 * stand er über dem Geltungsbereich-Absatz und war damit die zweite Einleitung
 * hintereinander; vor allem aber war er eine **zweite Fassung derselben
 * Aussagen** — wer § 4 ändert und den Kasten stehen lässt, hat zwei Antworten
 * auf dieselbe Frage, und die weichere steht oben. Dieselbe Überlegung wie bei
 * den Fristen (`lib/legal.test.ts`), nur ohne Test, der sie halten könnte.
 */
export function LegalNote({ children }: { readonly children: ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "var(--sp-3)",
        padding: "var(--sp-4) var(--sp-5)",
        background: "var(--surface-sunken)",
        borderRadius: "var(--radius-md)",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          marginTop: 7,
          flex: "none",
          borderRadius: "var(--radius-pill)",
          background: "var(--hm-green-500)",
        }}
      />
      <p
        style={{
          margin: 0,
          maxWidth: "78ch",
          fontSize: 14,
          lineHeight: 1.65,
          color: "var(--text-body)",
        }}
      >
        {children}
      </p>
    </div>
  );
}

/** Ein Block aus Etikett und mehrzeiliger Angabe — die Spalten im Impressum. */
export interface LegalFact {
  readonly label: string;
  readonly lines: readonly ReactNode[];
  /**
   * Eine leisere Zeile darunter — im Impressum der Zusatz „keine
   * Support-Hotline".
   *
   * **Sie steht abgesetzt und nicht in Klammern hinter der Nummer.** In der
   * Zeile gelesen wirkte sie wie ein Teil der Angabe; darunter ist sie, was sie
   * ist: eine Einordnung, wofür der Weg gedacht ist. Dass er für rechtliche
   * Anliegen offensteht, bleibt davon unberührt — und muss es, sonst erfüllte
   * die Nummer § 5 Abs. 1 Nr. 2 DDG nicht.
   */
  readonly note?: string;
}

export function LegalFacts({ items }: { readonly items: readonly LegalFact[] }) {
  return (
    <div
      style={{
        display: "grid",
        // `min(100%, 280px)`: Ohne das `min` überschreitet eine Spalte auf einem
        // schmalen Bildschirm die Fläche, und die Seite scrollt seitwärts.
        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
        gap: 28,
      }}
    >
      {items.map((item) => (
        <div key={item.label} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 700,
              letterSpacing: "var(--tracking-caps)",
              textTransform: "uppercase",
              color: "var(--text-faint)",
            }}
          >
            {item.label}
          </div>
          <div style={{ fontSize: 15, lineHeight: 1.65, color: "var(--text-body)" }}>
            {item.lines.map((line, index) => (
              // Die Zeilen einer Angabe stehen fest und in fester Reihenfolge;
              // ein anderer Schlüssel als der Index wäre hier eine Erfindung.
              <div key={index}>{line}</div>
            ))}
          </div>
          {item.note !== undefined && (
            <div style={{ fontSize: 12.5, lineHeight: 1.5, color: "var(--text-muted)" }}>
              {item.note}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Die gestreifte Tabelle aus dem Entwurf — Registerangaben, Speicherdauern.
 *
 * **Ein echtes `<table>` und kein Raster aus `div`s**, obwohl der Entwurf
 * Letzteres zeigt: Was hier steht, ist tabellarisch, und ein Screenreader
 * liest aus einem Raster keine Zuordnung von Kopf zu Zelle vor. Die
 * Spaltenbreiten kommen deshalb aus `table-layout` statt aus `grid`.
 */
export function LegalTable({
  head,
  rows,
}: {
  /** Die Kopfzeile. Fehlt sie, steht die linke Spalte für sich. */
  readonly head?: readonly [string, string];
  readonly rows: readonly (readonly [ReactNode, ReactNode])[];
}) {
  return (
    <div
      style={{
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-md)",
        overflow: "hidden",
      }}
    >
      {/**
       * **`overflow-wrap: anywhere` auf der ganzen Tabelle.** Bei `table-layout:
       * fixed` sind die beiden Spalten auf einem Telefon etwa 140 Pixel breit;
       * eine E-Mail-Adresse oder eine URL ohne Leerzeichen darin passt nicht
       * hinein und ragte über den Rand der Karte hinaus — die Angabe wäre nicht
       * mehr zu lesen, und das an einer Stelle, an der sie stehen **muss**
       * (§ 5 DDG). Ein waagerechter Bildlauf um den Kasten wäre der zweite Weg
       * und der schlechtere: Was rechts hinausragt, sucht dort niemand.
       */}
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          tableLayout: "fixed",
          overflowWrap: "anywhere",
        }}
      >
        {head !== undefined && (
          <thead>
            <tr>
              {head.map((cell) => (
                <th
                  key={cell}
                  scope="col"
                  style={{
                    textAlign: "left",
                    padding: "12px 18px",
                    background: "var(--surface-inset)",
                    borderBottom: "1px solid var(--border-subtle)",
                    fontSize: 11.5,
                    fontWeight: 700,
                    letterSpacing: "var(--tracking-caps)",
                    textTransform: "uppercase",
                    color: "var(--text-faint)",
                  }}
                >
                  {cell}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map(([label, value], index) => (
            <tr
              key={index}
              style={{
                // Ohne Kopfzeile beginnt die Streifung bei der ersten Zeile, mit
                // Kopfzeile bei der zweiten — sonst stünde ein grauer Streifen
                // unter dem grauen Kopf.
                background:
                  (head === undefined ? index : index + 1) % 2 === 0
                    ? "var(--surface-inset)"
                    : "transparent",
              }}
            >
              <th
                scope="row"
                style={{
                  textAlign: "left",
                  fontWeight: 400,
                  padding: "13px 18px",
                  borderBottom:
                    index === rows.length - 1 ? "none" : "1px solid var(--border-subtle)",
                  fontSize: 13.5,
                  lineHeight: 1.5,
                  color: "var(--text-muted)",
                  verticalAlign: "top",
                }}
              >
                {label}
              </th>
              <td
                style={{
                  padding: "13px 18px",
                  borderBottom:
                    index === rows.length - 1 ? "none" : "1px solid var(--border-subtle)",
                  fontSize: 14,
                  lineHeight: 1.5,
                  fontWeight: 600,
                  color: "var(--text-strong)",
                  verticalAlign: "top",
                }}
              >
                {value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Ein Verweis nach draußen — `mailto:` und `tel:`.
 *
 * **Kein `LegalLink`:** Der greift den Klick ab und ruft `navigate`, was für ein
 * Schema außerhalb der Anwendung falsch wäre. Und kein `target="_blank"`: Ein
 * Mailprogramm öffnet sich ohnehin nebenher, ein leerer Tab bliebe zurück.
 */
export function LegalExternal({
  href,
  children,
}: {
  readonly href: string;
  readonly children: ReactNode;
}) {
  return (
    <a href={href} style={{ color: "var(--text-brand)", textDecoration: "none" }}>
      {children}
    </a>
  );
}

/** Eine Angabe in Monoschrift — Registernummer, Umsatzsteuer-ID, WEEE-Nummer. */
export function LegalCode({ children }: { readonly children: ReactNode }) {
  return <span style={{ fontFamily: "var(--font-mono)" }}>{children}</span>;
}

/**
 * Ein Link innerhalb der Anwendung — als echtes `<a href>` und nicht als `div`
 * mit `onClick`, wie überall sonst in dieser Oberfläche (HM-038).
 */
export function LegalLink({
  to,
  children,
  style,
  ariaCurrent = false,
}: {
  readonly to: Route;
  readonly children: ReactNode;
  readonly style?: React.CSSProperties;
  readonly ariaCurrent?: boolean;
}) {
  return (
    <a
      href={routePath(to)}
      aria-current={ariaCurrent ? "page" : undefined}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        navigate(to);
      }}
      style={{ color: "var(--text-brand)", textDecoration: "none", fontWeight: 600, ...style }}
    >
      {children}
    </a>
  );
}
